import { Box, FormControlLabel, FormGroup, Slider, Switch, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';
import { UserContext } from '../Context/UserContext/UserContextProvider';
import { FilterAction, FitOrReliabilityFilterObject } from '../types/reducers/filterReducer';

export default function FitAndReliabilityFilterSlider({
  filterLabel,
  filterObject,
  dispatchFilter,
}: {
  filterLabel: 'fit' | 'reliability';
  filterObject: FitOrReliabilityFilterObject;
  dispatchFilter: React.Dispatch<FilterAction>;
}) {
  const location = useLocation();
  const { userDetailsState } = useContext(UserContext);
  const { addLog } = useContext(LoggingContext);
  const [disabledState, setDisabledState] = React.useState(filterObject.disabled);
  const [value, setValue] = React.useState<number>(filterObject.min);

  // update localState on Context update
  useEffect(() => {
    if (filterObject.disabled !== disabledState) setDisabledState(filterObject.disabled);
    if (filterObject.min !== value) setValue(filterObject.min);
  }, [filterObject]);

  // Debounce update to filterState from local state
  useEffect(() => {
    function updateWhenCheckboxClicked() {
      // Return out if local state matches filterState
      if (filterObject.min === value) return;
      dispatchFilter({
        type: 'update fit/reliability slider',
        filterLabel,
        min: value,
      });
    }
    const delayDebounceFn = setTimeout(() => {
      updateWhenCheckboxClicked();
    }, 150);
    return () => clearTimeout(delayDebounceFn);
  }, [value]);

  // Debounce update for disable/enable slider from local state
  useEffect(() => {
    function updateWhenDisabledToggled() {
      // If the disabled state is already equal to the filterState then return
      if (filterObject.disabled === disabledState) return;
      if (!disabledState) {
        // If we enable slider (disabledState=false), then update slider va;ie:
        dispatchFilter({
          type: 'update fit/reliability slider',
          filterLabel,
          min: value,
        });
      } else {
        dispatchFilter({
          type: 'disable fit or reliability slider',
          filterLabel,
        });
      }
    }
    const delayDebounceFn = setTimeout(() => {
      updateWhenDisabledToggled();
    }, 100);
    return () => clearTimeout(delayDebounceFn);
  }, [disabledState]);

  function convertToReadableValue(initialValue: number): number {
    if (userDetailsState.unitcm === 1) {
      return Math.round(initialValue);
    }
    return Math.round((initialValue / 2.57) * 2) / 2;
  }

  return (
    <Box sx={{ width: '80%', margin: 'auto' }}>
      <br />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          id={`input-slider-${filterLabel}`}
          variant='subtitle2'
          gutterBottom
          sx={{ textTransform: 'capitalize', alignSelf: 'center', fontWeight: 'bold' }}
        >
          {`${filterLabel}: (${convertToReadableValue(value)})`}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={!disabledState}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  addLog(
                    `Clicked enable/disable button for ${filterLabel} slider`, location.pathname
                  );
                  setDisabledState(!event.target.checked);
                }}
                color='success'
              />
            }
            labelPlacement='start'
            label={
              <Typography
                id={`input-label-${filterLabel}`}
                variant='caption'
                gutterBottom
                sx={{
                  textTransform: 'capitalize',
                }}
                color='success'
              >
                {`${disabledState ? 'Disabled' : 'Enabled'}`}
              </Typography>
            }
          />
        </FormGroup>
      </Box>
      <Slider
        getAriaLabel={() => filterLabel}
        value={value}
        onChange={(event: Event, newValue: number | number[]) => {
          if (typeof newValue === 'number') {
            setValue(newValue);
          }
        }}
        valueLabelDisplay='auto'
        disableSwap
        min={0}
        max={100}
        disabled={disabledState}
      />
    </Box>
  );
}
